import { useState, useEffect } from 'react';
import { X, Target, TrendingUp, Users, CheckCircle2, Zap } from 'lucide-react';
import { sleep } from '../utils/typeSimulation';

// ── Real 30-day calendar matching platform output ─────────────────────────
const CALENDAR = [
  { d:1,  p:'IG', f:'Carousel', t:'E'  },  { d:2,  p:'FB', f:'Post',     t:'P'  },
  { d:3,  p:'IG', f:'Reel',     t:'EN' },  { d:4,  p:'FB', f:'Post',     t:'I'  },
  { d:5,  p:'IG', f:'Carousel', t:'E'  },  { d:6,  p:'IG', f:'Story',    t:'P'  },
  { d:7,  p:'FB', f:'Post',     t:'E'  },  { d:8,  p:'IG', f:'Reel',     t:'EN' },
  { d:9,  p:'FB', f:'Post',     t:'P'  },  { d:10, p:'IG', f:'Carousel', t:'I'  },
  { d:11, p:'FB', f:'Post',     t:'E'  },  { d:12, p:'IG', f:'Reel',     t:'EN' },
  { d:13, p:'IG', f:'Story',    t:'P'  },  { d:14, p:'FB', f:'Post',     t:'I'  },
  { d:15, p:'IG', f:'Carousel', t:'I'  },  { d:16, p:'FB', f:'Post',     t:'E'  },
  { d:17, p:'IG', f:'Reel',     t:'EN' },  { d:18, p:'FB', f:'Post',     t:'P'  },
  { d:19, p:'IG', f:'Carousel', t:'E'  },  { d:20, p:'IG', f:'Story',    t:'P'  },
  { d:21, p:'FB', f:'Post',     t:'I'  },  { d:22, p:'IG', f:'Reel',     t:'EN' },
  { d:23, p:'FB', f:'Post',     t:'P'  },  { d:24, p:'IG', f:'Carousel', t:'E'  },
  { d:25, p:'FB', f:'Post',     t:'I'  },  { d:26, p:'IG', f:'Reel',     t:'EN' },
  { d:27, p:'IG', f:'Carousel', t:'P'  },  { d:28, p:'FB', f:'Post',     t:'E'  },
  { d:29, p:'IG', f:'Story',    t:'I'  },  { d:30, p:'FB', f:'Post',     t:'P'  },
];

// Content type styling matching real platform
const TYPE_STYLE = {
  E:  { bg: 'bg-blue-500',   label: 'E', title: 'Educational'  },
  EN: { bg: 'bg-purple-500', label: 'EN', title: 'Entertainment' },
  I:  { bg: 'bg-amber-500',  label: 'I', title: 'Inspirational' },
  P:  { bg: 'bg-green-500',  label: 'P', title: 'Promotional'   },
};

// Platform colours
const PLAT_STYLE = {
  IG: 'text-pink-600',
  FB: 'text-blue-600',
};

// AI generation steps (real platform labels)
const AI_STEPS = [
  'Analysing your Brand DNA...',
  'Studying your competitors...',
  'Crafting content pillars...',
  'Building channel strategy...',
  'Generating 30-day calendar...',
  'Finalising your strategy...',
];

// Strategy output data
const STRATEGY = {
  smartGoal: 'Publish 30 Instagram & Facebook posts within 30 days — increasing brand awareness and audience engagement for The Bloom Bakery.',
  positioning: 'For coffee lovers and busy professionals in Manchester who want a quality third-place experience, The Bloom Bakery is the neighbourhood cafe that combines artisan quality with genuine community warmth.',
  channels: [
    { name: 'Instagram', allocation: '60%', primary: true },
    { name: 'Facebook',  allocation: '30%', primary: true },
    { name: 'Short Video', allocation: '10%', primary: false },
  ],
  phases: [
    {
      phase: 'Strategy & Setup',
      goals: ['Build Brand DNA profile & customer persona', 'Generate SMART goal & brand positioning', 'Create 30-day content calendar with daily briefs'],
      kpis: { posts: '30', platforms: '2', time: '15 min' },
      color: 'blue',
    },
    {
      phase: 'Create & Design',
      goals: ['Write AI captions & hashtags for each post', 'Design graphics using Design Studio (12 templates)', 'Film guided video content (4-step workflow)'],
      kpis: { posts: '30', templates: '12', styles: '14' },
      color: 'purple',
    },
    {
      phase: 'Publish & Track',
      goals: ['Publish directly to Instagram & Facebook', 'Track posting streak & campaign completion', 'Download PDF campaign report'],
      kpis: { posts: '30', streak: '30 days', report: 'PDF' },
      color: 'green',
    },
  ],
};

export default function StrategyPanel({ onClose }) {
  const [phase, setPhase]  = useState('goals');   // 'goals' | 'generating' | 'calendar' | 'summary'
  const [aiStep, setAiStep] = useState(0);
  const [aiDone, setAiDone] = useState([]);
  const [calVisible, setCalVisible] = useState(0);  // how many calendar days shown
  const [stratSections, setStratSections] = useState(0);

  // Goals form state
  const [goals] = useState({
    primaryGoal: 'Increase brand awareness',
    igFollowers: '1,240',
    fbFollowers: '820',
  });

  useEffect(() => {
    const run = async () => {
      // Phase 1: show pre-filled goals form briefly
      await sleep(1200);
      setPhase('generating');

      // Phase 2: step through AI generation
      for (let i = 0; i < AI_STEPS.length; i++) {
        setAiStep(i);
        await sleep(900);
        setAiDone(prev => [...prev, i]);
      }
      await sleep(600);
      setPhase('calendar');

      // Phase 3: reveal calendar days progressively
      for (let n = 1; n <= CALENDAR.length; n++) {
        setCalVisible(n);
        await sleep(55);
      }
      await sleep(500);
      setPhase('summary');

      // Phase 4: reveal strategy sections
      for (let i = 1; i <= 4; i++) {
        setStratSections(i);
        await sleep(600);
      }
    };
    run();
  }, []);

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-4xl bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">
        <div className="h-20 bg-white" />

        {/* ── Sticky header ── */}
        <div className="sticky top-20 bg-white border-b-2 border-gray-200 p-5 z-20">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-0.5">AI Strategy Generation</h2>
              <p className="text-sm text-gray-500">30-day personalised marketing strategy — generated in seconds</p>
            </div>
            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all opacity-60 hover:opacity-100">
              <X size={24} />
            </button>
          </div>

          {/* Phase pill */}
          <div className="mt-3 flex gap-2">
            {[
              { id: 'goals',      label: '1  Goals' },
              { id: 'generating', label: '2  AI Generation' },
              { id: 'calendar',   label: '3  30-Day Calendar' },
              { id: 'summary',    label: '4  Strategy' },
            ].map((p, i) => {
              const phases = ['goals', 'generating', 'calendar', 'summary'];
              const current = phases.indexOf(phase);
              const pIdx    = phases.indexOf(p.id);
              return (
                <div key={p.id} className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                  pIdx < current ? 'bg-green-100 text-green-700' :
                  pIdx === current ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'
                }`}>
                  {pIdx < current ? '✓ ' : ''}{p.label}
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-5 space-y-6">

          {/* ══ PHASE 1: Goals & social presence ══ */}
          {(phase === 'goals' || ['generating','calendar','summary'].includes(phase)) && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Target size={15} className="text-blue-600" />
                Marketing Goals & Online Presence
                {phase !== 'goals' && <span className="text-xs text-green-600 font-semibold">✓ Saved</span>}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <FieldLabel>Primary Goal</FieldLabel>
                  <div className="px-3 py-2 bg-blue-50 border-2 border-blue-200 rounded-lg text-sm font-semibold text-blue-800">{goals.primaryGoal}</div>
                </div>
                <div>
                  <FieldLabel>Instagram Followers</FieldLabel>
                  <div className="px-3 py-2 bg-pink-50 border-2 border-pink-200 rounded-lg text-sm font-semibold text-pink-800">{goals.igFollowers}</div>
                </div>
                <div>
                  <FieldLabel>Facebook Followers</FieldLabel>
                  <div className="px-3 py-2 bg-blue-50 border-2 border-blue-200 rounded-lg text-sm font-semibold text-blue-800">{goals.fbFollowers}</div>
                </div>
              </div>
            </div>
          )}

          {/* ══ PHASE 2: AI Generation ══ */}
          {(phase === 'generating' || ['calendar','summary'].includes(phase)) && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Zap size={15} className="text-blue-600" />
                AI is analysing your profile
                {phase !== 'generating' && <span className="text-xs text-green-600 font-semibold">✓ Complete</span>}
              </h3>
              <div className="bg-slate-900 rounded-xl p-4 space-y-2.5 font-mono">
                {/* Progress bar */}
                <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden mb-3">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-700"
                    style={{ width: `${phase === 'generating' ? ((aiDone.length / AI_STEPS.length) * 100) : 100}%` }}
                  />
                </div>
                {AI_STEPS.map((label, i) => {
                  const done = aiDone.includes(i);
                  const active = i === aiStep && phase === 'generating';
                  return (
                    <div key={i} className={`flex items-center gap-2.5 text-xs transition-all duration-300 ${done || phase !== 'generating' ? 'opacity-100' : active ? 'opacity-100' : 'opacity-30'}`}>
                      {(done || phase !== 'generating')
                        ? <CheckCircle2 size={13} className="text-green-400 flex-shrink-0" />
                        : active
                        ? <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        : <div className="w-3 h-3 rounded-full border-2 border-slate-600 flex-shrink-0" />
                      }
                      <span className={done || phase !== 'generating' ? 'text-green-300' : active ? 'text-blue-300' : 'text-slate-500'}>
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══ PHASE 3: 30-Day Calendar Preview ══ */}
          {(phase === 'calendar' || phase === 'summary') && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                30-Day Content Calendar
                <span className="text-xs text-gray-400 font-normal ml-auto flex gap-3">
                  {Object.entries(TYPE_STYLE).map(([k, v]) => (
                    <span key={k} className="flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${v.bg}`} />
                      {v.title}
                    </span>
                  ))}
                </span>
              </h3>
              <div className="grid grid-cols-10 gap-1">
                {CALENDAR.map((day, idx) => {
                  if (idx >= calVisible) return <div key={day.d} className="aspect-square rounded" />;
                  const ts = TYPE_STYLE[day.t];
                  return (
                    <div key={day.d} title={`Day ${day.d} — ${day.p} ${day.f} (${ts.title})`}
                      className={`relative aspect-square ${ts.bg} rounded flex flex-col items-center justify-center cursor-default group transition-all hover:scale-110 hover:z-10`}
                    >
                      <span className="text-[9px] font-bold text-white/70 leading-none">{day.p}</span>
                      <span className="text-[9px] text-white/90 leading-none">{day.d}</span>
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[9px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-20 transition-opacity">
                        {day.f} · {ts.title}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <span>30 posts across Instagram & Facebook</span>
                <span className="font-semibold text-green-600">{calVisible}/30 planned</span>
              </div>
            </div>
          )}

          {/* ══ PHASE 4: Strategy Summary ══ */}
          {phase === 'summary' && (
            <div className="space-y-4">

              {/* SMART Goal */}
              {stratSections >= 1 && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl animate-fadeIn">
                  <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-1.5">SMART Goal</p>
                  <p className="text-sm text-gray-800 leading-relaxed font-medium">{STRATEGY.smartGoal}</p>
                </div>
              )}

              {/* Positioning */}
              {stratSections >= 2 && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-xl animate-fadeIn">
                  <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest mb-1.5">Brand Positioning Statement</p>
                  <p className="text-xs text-gray-700 leading-relaxed italic">"{STRATEGY.positioning}"</p>
                </div>
              )}

              {/* Channel strategy */}
              {stratSections >= 3 && (
                <div className="animate-fadeIn">
                  <p className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">Channel Strategy (60/30/10)</p>
                  <div className="flex gap-2">
                    {STRATEGY.channels.map(ch => (
                      <div key={ch.name} className={`flex-1 text-center py-2 px-3 rounded-lg border-2 ${
                        ch.primary ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <p className="text-xs font-bold text-gray-900">{ch.name}</p>
                        <p className={`text-xs font-semibold ${ch.primary ? 'text-blue-600' : 'text-gray-500'}`}>{ch.allocation}</p>
                        {ch.primary && <span className="text-[9px] text-blue-500 font-medium">Primary</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 3-phase execution plan */}
              {stratSections >= 4 && (
                <div className="space-y-3 animate-fadeIn">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Execution Plan</p>
                  {STRATEGY.phases.map((ph, i) => {
                    const PhIcon = i === 0 ? Target : i === 1 ? TrendingUp : Users;
                    const clr = ph.color === 'blue' ? { hdr: 'bg-blue-600', border: 'border-blue-200', bg: 'bg-blue-50', ico: 'text-blue-600', kpi: 'bg-blue-100 text-blue-700' }
                              : ph.color === 'purple' ? { hdr: 'bg-purple-600', border: 'border-purple-200', bg: 'bg-purple-50', ico: 'text-purple-600', kpi: 'bg-purple-100 text-purple-700' }
                              : { hdr: 'bg-green-600', border: 'border-green-200', bg: 'bg-green-50', ico: 'text-green-600', kpi: 'bg-green-100 text-green-700' };
                    return (
                      <div key={i} className={`border ${clr.border} ${clr.bg} rounded-xl overflow-hidden`}>
                        <div className={`${clr.hdr} px-4 py-2 flex items-center gap-2`}>
                          <PhIcon size={13} className="text-white" />
                          <span className="text-xs font-bold text-white">Phase {i + 1}: {ph.phase}</span>
                        </div>
                        <div className="px-4 py-3">
                          <ul className="space-y-1 mb-2">
                            {ph.goals.map((g, j) => (
                              <li key={j} className="flex items-start gap-1.5 text-xs text-gray-700">
                                <CheckCircle2 size={11} className={`${clr.ico} mt-0.5 flex-shrink-0`} />
                                {g}
                              </li>
                            ))}
                          </ul>
                          <div className="flex gap-2">
                            {Object.entries(ph.kpis).map(([k, v]) => (
                              <span key={k} className={`text-xs px-2 py-0.5 rounded font-semibold ${clr.kpi}`}>{v}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {stratSections >= 4 && (
                <div className="flex items-center justify-center pt-2">
                  <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2">
                    Proceed to Design & Create
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}

        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-400 text-center">Simulated demo — Real AI generation uses advanced models trained on industry, competitors, and market trends.</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slide-in 0.3s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
      `}</style>
    </>
  );
}

function FieldLabel({ children }) {
  return <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">{children}</label>;
}
